/**
 * POSVoidInvoice - Void/Cancel Invoice functionality for POS
 * Allows cashiers to void an invoice immediately after creation (within 5 minutes)
 */
import { useState, useEffect } from 'react';
import { 
  Search, ArrowLeft, XCircle, AlertTriangle, Check, 
  Printer, Clock, X, Loader2, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { posApi } from '@/utils/apiClient';
import { printFiscalReceipt } from '@/components/frcs/FiscalReceipt';

// Time limit for voiding (5 minutes in milliseconds)
const VOID_TIME_LIMIT_MS = 5 * 60 * 1000;

export default function POSVoidInvoice({ onClose, cashierName = 'Admin' }) {
  const [step, setStep] = useState('search'); // search, confirm, complete
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [voidInvoice, setVoidInvoice] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Load recent invoices on mount (only from last 5 minutes)
  useEffect(() => {
    fetchRecentVoidableInvoices();
  }, []);

  // Update time remaining every second
  useEffect(() => {
    if (!originalInvoice) return;
    
    const interval = setInterval(() => {
      const invoiceTime = parseInvoiceTime(originalInvoice.sdc_time);
      if (invoiceTime) {
        const elapsed = Date.now() - invoiceTime.getTime();
        const remaining = VOID_TIME_LIMIT_MS - elapsed;
        
        if (remaining <= 0) {
          setTimeRemaining(0);
          toast.error('Void time limit expired');
          setStep('search');
          setOriginalInvoice(null);
        } else {
          setTimeRemaining(remaining);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [originalInvoice]);

  // Parse invoice time from SDC format (e.g., "27/05/2026 08:55:30 AM")
  const parseInvoiceTime = (sdcTime) => {
    if (!sdcTime) return null;
    try {
      // Parse format: DD/MM/YYYY HH:MM:SS AM/PM
      const parts = sdcTime.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2}):?(\d{2})?\s*(AM|PM)?/i);
      if (!parts) return null;
      
      let [, day, month, year, hours, minutes, seconds = '00', ampm] = parts;
      hours = parseInt(hours);
      
      if (ampm?.toUpperCase() === 'PM' && hours < 12) hours += 12;
      if (ampm?.toUpperCase() === 'AM' && hours === 12) hours = 0;
      
      return new Date(year, month - 1, day, hours, parseInt(minutes), parseInt(seconds));
    } catch {
      return null;
    }
  };

  // Check if invoice is within void time limit
  const isWithinVoidLimit = (invoice) => {
    const invoiceTime = parseInvoiceTime(invoice.sdc_time);
    if (!invoiceTime) return false;
    
    const elapsed = Date.now() - invoiceTime.getTime();
    return elapsed <= VOID_TIME_LIMIT_MS;
  };

  const fetchRecentVoidableInvoices = async () => {
    try {
      const res = await posApi.get('/frcs-vms/fiscal-invoices?limit=20&invoice_type=NS');
      // Filter to only show invoices within void time limit
      const voidable = (res.data.invoices || []).filter(inv => 
        isWithinVoidLimit(inv) && !['NR', 'AR', 'CR', 'TR'].includes(inv.invoice_type)
      );
      setRecentInvoices(voidable);
    } catch (err) {
      console.error('Failed to fetch recent invoices:', err);
    }
  };

  const searchInvoice = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter an SDC number');
      return;
    }

    setSearching(true);
    try {
      const res = await posApi.get(`/frcs-vms/fiscal-invoice/${searchQuery.trim()}`);
      const invoice = res.data;
      
      // Check if it's already a refund/void
      if (['NR', 'AR', 'CR', 'TR'].includes(invoice.invoice_type)) {
        toast.error('Cannot void a refund invoice');
        return;
      }
      
      // Check if within time limit
      if (!isWithinVoidLimit(invoice)) {
        toast.error('Invoice is older than 5 minutes. Use Refund instead.');
        return;
      }
      
      setOriginalInvoice(invoice);
      setStep('confirm');
    } catch (err) {
      toast.error('Invoice not found');
    } finally {
      setSearching(false);
    }
  };

  const selectInvoice = (invoice) => {
    if (['NR', 'AR', 'CR', 'TR'].includes(invoice.invoice_type)) {
      toast.error('Cannot void a refund invoice');
      return;
    }
    if (!isWithinVoidLimit(invoice)) {
      toast.error('Invoice is older than 5 minutes. Use Refund instead.');
      return;
    }
    setOriginalInvoice(invoice);
    setStep('confirm');
  };

  const processVoid = async () => {
    if (!originalInvoice) return;
    
    if (!voidReason.trim()) {
      toast.error('Please enter a reason for voiding');
      return;
    }

    // Double-check time limit
    if (!isWithinVoidLimit(originalInvoice)) {
      toast.error('Void time limit has expired. Use Refund instead.');
      return;
    }

    setProcessing(true);
    try {
      // Void is essentially a refund with "VOID" reason
      const params = new URLSearchParams({
        cashier_name: cashierName,
        reason: `VOID: ${voidReason.trim()}`
      });

      const res = await posApi.post(`/frcs-vms/refund/${originalInvoice.sdc_number}?${params.toString()}`);
      
      if (res.data.success) {
        setVoidInvoice(res.data.fiscal_invoice);
        setStep('complete');
        toast.success('Invoice voided successfully');
        
        // Auto-print void receipt
        setTimeout(() => {
          printFiscalReceipt(res.data.fiscal_invoice);
        }, 500);
      } else {
        throw new Error(res.data.error || 'Void failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Void processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const resetFlow = () => {
    setStep('search');
    setSearchQuery('');
    setOriginalInvoice(null);
    setVoidInvoice(null);
    setVoidReason('');
    setTimeRemaining(null);
    fetchRecentVoidableInvoices();
  };

  const formatTimeRemaining = (ms) => {
    if (!ms || ms <= 0) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Search Step
  if (step === 'search') {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div className="flex items-center gap-3 md:gap-4">
              <Button 
                variant="ghost" 
                onClick={onClose}
                className="text-white hover:bg-white/10 p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">Void Invoice</h1>
                <p className="text-gray-400 text-xs md:text-sm">Cancel invoice within 5 minutes</p>
              </div>
            </div>
            <XCircle className="w-6 h-6 md:w-8 md:h-8 text-orange-400" />
          </div>

          {/* Time Limit Warning */}
          <div className="bg-orange-900/30 border border-orange-600 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-orange-400" />
              <div>
                <p className="font-medium text-orange-300">5 Minute Time Limit</p>
                <p className="text-sm text-orange-200/80">
                  Invoices can only be voided within 5 minutes of creation. 
                  For older invoices, use Refund instead.
                </p>
              </div>
            </div>
          </div>

          {/* Search Box */}
          <div className="bg-gray-800 rounded-xl p-4 md:p-6 mb-6">
            <label className="block text-sm text-gray-400 mb-2">
              Enter SDC Number
            </label>
            <div className="flex gap-2 md:gap-3">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchInvoice()}
                placeholder="e.g., AX3K624F-787FED18-1"
                className="flex-1 bg-gray-700 border-gray-600 text-white text-base md:text-lg font-mono"
                data-testid="void-search-input"
              />
              <Button 
                onClick={searchInvoice}
                disabled={searching}
                className="bg-orange-600 hover:bg-orange-700 px-4 md:px-6"
                data-testid="void-search-btn"
              >
                {searching ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Recent Voidable Invoices */}
          <div className="bg-gray-800 rounded-xl p-4 md:p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-400" />
              Recent (Voidable)
            </h3>
            {recentInvoices.length === 0 ? (
              <p className="text-gray-500 text-center py-6 md:py-8">
                No voidable invoices (within 5 minutes)
              </p>
            ) : (
              <div className="space-y-2">
                {recentInvoices.map((invoice) => {
                  const invoiceTime = parseInvoiceTime(invoice.sdc_time);
                  const remaining = invoiceTime ? VOID_TIME_LIMIT_MS - (Date.now() - invoiceTime.getTime()) : 0;
                  
                  return (
                    <button
                      key={invoice.sdc_number}
                      onClick={() => selectInvoice(invoice)}
                      className="w-full flex items-center justify-between p-3 md:p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
                      data-testid={`voidable-invoice-${invoice.sdc_number}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs md:text-sm text-orange-400 truncate">
                          {invoice.sdc_number}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          {invoice.cashier}
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="font-bold text-sm md:text-base">${invoice.total?.toFixed(2)}</p>
                        <p className="text-xs text-orange-400">
                          {formatTimeRemaining(remaining)} left
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Confirm Step
  if (step === 'confirm' && originalInvoice) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center gap-3 md:gap-4">
              <Button 
                variant="ghost" 
                onClick={resetFlow}
                className="text-white hover:bg-white/10 p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">Confirm Void</h1>
                <p className="text-gray-400 text-xs md:text-sm">Cancel this invoice</p>
              </div>
            </div>
            
            {/* Time Remaining Counter */}
            {timeRemaining !== null && (
              <div className={`px-3 md:px-4 py-2 rounded-lg font-mono text-lg md:text-xl font-bold ${
                timeRemaining < 60000 ? 'bg-red-600 animate-pulse' : 'bg-orange-600'
              }`}>
                {formatTimeRemaining(timeRemaining)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Original Invoice */}
            <div className="bg-gray-800 rounded-xl p-4 md:p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-orange-400" />
                Invoice to Void
              </h3>
              
              <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-400">SDC Number:</span>
                  <span className="font-mono text-xs md:text-sm">{originalInvoice.sdc_number}</span>
                </div>
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-400">Date:</span>
                  <span>{originalInvoice.sdc_time}</span>
                </div>
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-400">Cashier:</span>
                  <span>{originalInvoice.cashier}</span>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm text-gray-400 mb-2">Items:</h4>
                {originalInvoice.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span className="truncate mr-2">{item.quantity}x {item.name}</span>
                    <span className="flex-shrink-0">${item.total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-700 mt-4 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total to Void:</span>
                  <span className="text-orange-400">-${originalInvoice.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Void Details */}
            <div className="bg-gray-800 rounded-xl p-4 md:p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Void Reason
              </h3>

              <div className="mb-4 md:mb-6">
                <label className="block text-sm text-gray-400 mb-2">
                  Reason for Void *
                </label>
                <textarea
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="e.g., Wrong item entered, Customer changed mind..."
                  className="w-full h-24 md:h-32 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none text-sm md:text-base"
                  data-testid="void-reason-input"
                />
              </div>

              <div className="bg-orange-900/30 border border-orange-600 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
                <div className="flex items-start gap-2 md:gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-orange-300 text-sm md:text-base">Warning</p>
                    <p className="text-xs md:text-sm text-orange-200/80 mt-1">
                      This will create a void/refund invoice and cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 md:gap-3">
                <Button
                  variant="outline"
                  onClick={resetFlow}
                  className="flex-1 border-gray-600 text-white hover:bg-gray-700 text-sm md:text-base"
                >
                  Cancel
                </Button>
                <Button
                  onClick={processVoid}
                  disabled={processing || !voidReason.trim()}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-sm md:text-base"
                  data-testid="process-void-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin mr-2" />
                      <span className="hidden sm:inline">Voiding...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                      Void Invoice
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Complete Step
  if (step === 'complete' && voidInvoice) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-900 to-orange-950 flex items-center justify-center p-4 md:p-6">
        <div className="text-center text-white max-w-md w-full">
          <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-4 md:mb-6 rounded-full bg-orange-500 flex items-center justify-center">
            <Check className="w-10 h-10 md:w-12 md:h-12" />
          </div>
          
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Invoice Voided</h1>
          <p className="text-orange-200 mb-4 md:mb-6 text-sm md:text-base">
            Void receipt has been printed
          </p>

          <div className="bg-white/10 rounded-xl p-4 md:p-6 mb-4 md:mb-6 text-left">
            <div className="space-y-2">
              <div className="flex justify-between text-sm md:text-base">
                <span className="text-orange-200">Void SDC:</span>
                <span className="font-mono text-xs md:text-sm">{voidInvoice.sdc_number}</span>
              </div>
              <div className="flex justify-between text-sm md:text-base">
                <span className="text-orange-200">Original:</span>
                <span className="font-mono text-xs md:text-sm">{voidInvoice.reference_number}</span>
              </div>
              <div className="flex justify-between text-lg md:text-xl font-bold">
                <span>Voided:</span>
                <span>-${voidInvoice.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              onClick={() => printFiscalReceipt(voidInvoice)}
              className="w-full bg-white/20 hover:bg-white/30 text-white"
              data-testid="print-void-btn"
            >
              <Printer className="w-5 h-5 mr-2" />
              Reprint Void Receipt
            </Button>
            <Button
              onClick={onClose}
              className="w-full bg-white text-orange-900 hover:bg-white/90"
              data-testid="close-void-btn"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
